import { ISdkContainer } from '../container/SdkContainer';
import { LogEntry, LogLevel, SdkConfig } from '../domain/types';
import { ILogger } from '../domain/interfaces/ILogger';
import { shouldCapture, getEventLogLevel } from '../utils/LogLevelFilter';

export class StackScope {
  private container: ISdkContainer;
  private config: SdkConfig;
  private sessionId: string;
  private isInitialized = false;
  private logger: ILogger;

  constructor(container: ISdkContainer, config: SdkConfig) {
    this.container = container;
    this.config = config;
    this.sessionId = this.generateSessionId();
    this.logger = container.getLogger();
  }

  init(): void {
    if (this.isInitialized) {
      this.logger.warn('Already initialized');
      return;
    }

    this.isInitialized = true;
    const transport = this.container.getTransport();
    
    const handleLog = (entry: LogEntry): void => {
      // Apply log level filtering
      const configuredLogLevel = this.config.logLevel || 'error';
      
      // For events with metadata, determine the appropriate log level
      let eventLogLevel = entry.level;
      if (entry.meta?.type) {
        eventLogLevel = getEventLogLevel(entry.meta.type, entry.meta, entry.level);
        // Override the entry level with the determined event level
        entry.level = eventLogLevel;
      }
      
      // Check if this log entry should be captured based on log level
      if (!shouldCapture(eventLogLevel, configuredLogLevel)) {
        return; // Skip this log entry
      }
      
      // Enrich with session ID
      if (!entry.meta) entry.meta = {};
      entry.meta.sessionId = this.sessionId;
      
      // Send to transport
      transport.send([entry]).catch(error => {
        if (this.config.debug) {
          this.logger.error('Transport error:', error);
        }
      });
    };

    // Start capture modules based on config
    if (this.config.captureConsole !== false) {
      this.container.getConsoleCapture().start(handleLog);
    }

    if (this.config.captureNetwork !== false) {
      this.container.getNetworkCapture().start(handleLog);
    }

    if (this.config.captureInteractions !== false) {
      this.container.getInteractionCapture().start(handleLog);
    }

    if (this.config.captureNavigation !== false) {
      this.container.getNavigationCapture().start(handleLog);
    }

    if (this.config.capturePerformance !== false) {
      this.container.getPerformanceCapture().start(handleLog);
    }

    if (this.config.captureVisibility !== false) {
      this.container.getVisibilityCapture().start(handleLog);
    }

    if (this.config.debug) {
      this.logger.info('Initialized with session ID:', this.sessionId);
    }

    // Flush logs on page unload
    window.addEventListener('beforeunload', () => {
      this.flush();
    });
  }

  log(level: LogLevel, msg: string, meta?: any): void {
    if (!this.isInitialized) {
      this.logger.warn('Not initialized. Call init() first.');
      return;
    }

    const entry: LogEntry = {
      level,
      msg,
      ts: new Date().toISOString(),
      meta: {
        ...meta,
        sessionId: this.sessionId,
        type: 'console',
        url: window.location.href
      }
    };

    this.container.getTransport().send([entry]).catch(error => {
      if (this.config.debug) {
        this.logger.error('Failed to send log:', error);
      }
    });
  }

  info(msg: string, meta?: any): void {
    this.log('info', msg, meta);
  }

  warn(msg: string, meta?: any): void {
    this.log('warn', msg, meta);
  }

  error(msg: string, meta?: any): void {
    this.log('error', msg, meta);
  }

  debug(msg: string, meta?: any): void {
    this.log('debug', msg, meta);
  }

  flush(): Promise<void> {
    return this.container.getTransport().flush();
  }

  stop(): void {
    if (!this.isInitialized) return;

    this.container.getConsoleCapture().stop();
    this.container.getNetworkCapture().stop();
    this.container.getInteractionCapture().stop();
    this.container.getNavigationCapture().stop();
    this.container.getPerformanceCapture().stop();
    this.container.getVisibilityCapture().stop();
    
    this.isInitialized = false;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  private generateSessionId(): string {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 9);
    return `sess_${timestamp}_${randomPart}`;
  }
}